"use client";

import { Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

// Define available transition strategies for testing
export type TransitionStrategy =
  | "opacity-fade" // Simple fade from poster to video
  | "pause-reset-play" // Wistia recommended: pause, reset to frame 0, then play
  | "wait-for-playing" // Wait until video actually starts playing
  | "layered-persistent"; // Poster stays in DOM, video layers on top

interface BackgroundVideoProps {
  video: string;
  className?: string;
  posterImageBase64?: string | null;
  transitionStrategy?: TransitionStrategy;
}

/**
 * BackgroundVideo component with seamless transition from poster to video
 *
 * Implements multiple transition strategies for testing which provides the best UX:
 * 1. "opacity-fade": Simple opacity transition (may have brief flash)
 * 2. "pause-reset-play": Wistia recommended technique (most seamless)
 * 3. "wait-for-playing": Waits for video to actually start playing
 * 4. "layered-persistent": Poster stays as background layer
 */
const BackgroundVideo: React.FC<BackgroundVideoProps> = ({
  video,
  className,
  posterImageBase64,
  transitionStrategy = "pause-reset-play" // Default to Wistia-recommended approach
}) => {
  const [videoOpacity, setVideoOpacity] = useState(0);
  const [posterVisible, setPosterVisible] = useState(true);
  const wistiaInitialized = useRef(false);

  /**
   * Strategy 1: Simple opacity fade
   * Simplest approach - may show brief flash if timing is off
   */
  const executeOpacityFade = (wistiaVideo: any) => {
    wistiaVideo.mute();
    wistiaVideo.time(1); // Start video at 1 second to skip initial loading
    wistiaVideo.play();

    // Wait brief moment for video to start, then fade in
    setTimeout(() => {
      setVideoOpacity(1);
      // Brief overlap to prevent white flash while video fades in
      setTimeout(() => setPosterVisible(false), 300);
    }, 888);
  };

  /**
   * Strategy 2: Pause-Reset-Play (Wistia Recommended)
   * Most seamless - pauses video at current position before fading in
   */
  const executePauseResetPlay = (wistiaVideo: any) => {
    // Set up one-time play event listener
    const handleFirstPlay = () => {
      // Immediately pause (video stays at 1.5 seconds where it started)
      wistiaVideo.pause();

      // Wait for video to stabilize before fading in
      setTimeout(() => {
        // Reset to 1.5 seconds to prevent any drift/jitter
        wistiaVideo.time(1.5);

        // Fade in the video
        setVideoOpacity(1);

        // After fade completes, resume playback and remove poster
        setTimeout(() => {
          wistiaVideo.play();
          setPosterVisible(false);
        }, 300); // Overlap time - poster stays visible while video fades in
      }, 888); // Wait before starting fade (let video stabilize)

      // Remove the event listener (one-time only)
      wistiaVideo.unbind("play", handleFirstPlay);
    };

    // Bind to play event
    wistiaVideo.bind("play", handleFirstPlay);

    // Start muted at 1.5 seconds and trigger autoplay
    wistiaVideo.mute();
    wistiaVideo.time(1.5); // Start video at 1.5 seconds to skip initial loading
    wistiaVideo.play(); // This will trigger the bind above
  };

  /**
   * Strategy 3: Wait for playing state
   * Waits until video actually starts playing before showing it
   */
  const executeWaitForPlaying = (wistiaVideo: any) => {
    const handlePlay = () => {
      setVideoOpacity(1);
      setTimeout(() => setPosterVisible(false), 300);
      wistiaVideo.unbind("play", handlePlay);
    };

    wistiaVideo.bind("play", handlePlay);
    wistiaVideo.mute();
    wistiaVideo.play();
  };

  /**
   * Strategy 4: Layered with persistent poster
   * Poster stays in DOM as background, video layers on top
   * Most resilient to edge cases
   */
  const executeLayeredPersistent = (wistiaVideo: any) => {
    wistiaVideo.mute();
    wistiaVideo.play();

    // Just fade in the video, poster stays underneath
    setTimeout(() => {
      setVideoOpacity(1);
    }, 100);

    // Don't hide poster - it stays as persistent background layer
  };

  /**
   * Handles Wistia video ready event with different transition strategies
   */
  const handleWistiaReady = React.useCallback(
    (wistiaVideo: any) => {
      try {
        // Hide captions if present
        if (wistiaVideo?.captions) {
          wistiaVideo.captions.hide();
        }

        // Execute the selected transition strategy
        switch (transitionStrategy) {
          case "opacity-fade":
            executeOpacityFade(wistiaVideo);
            break;

          case "pause-reset-play":
            executePauseResetPlay(wistiaVideo);
            break;

          case "wait-for-playing":
            executeWaitForPlaying(wistiaVideo);
            break;

          case "layered-persistent":
            executeLayeredPersistent(wistiaVideo);
            break;

          default:
            // Default to pause-reset-play
            executePauseResetPlay(wistiaVideo);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error in Wistia onReady handler:", error);
        // Fallback: just show the video
        setVideoOpacity(1);
      }
    },
    [transitionStrategy]
  );

  useEffect(() => {
    // Only initialize Wistia event listeners once
    if (!video || wistiaInitialized.current) {
      return undefined;
    }

    // Ensure Wistia _wq queue exists
    if (typeof window !== "undefined") {
      window._wq = window._wq || [];

      // Push Wistia command to queue
      window._wq.push({
        id: video,
        onReady: (wistiaVideo: any) => {
          handleWistiaReady(wistiaVideo);
        }
      });

      wistiaInitialized.current = true;
    }

    // Cleanup function
    return () => {
      // Wistia cleanup if needed
      wistiaInitialized.current = false;
    };
  }, [video, handleWistiaReady]);

  // Determine poster visibility based on strategy
  const shouldShowPoster = transitionStrategy === "layered-persistent" || posterVisible;

  return (
    <Box
      component="div"
      data-testid="background-video"
      className={className}
      id={video}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        minHeight: "628px",
        zIndex: 1,
        // Display poster image as background for instant loading
        backgroundImage: posterImageBase64 && shouldShowPoster ? `url("${posterImageBase64}")` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        // Video element styles
        "& > div": {
          opacity: videoOpacity,
          transition: "opacity 0.3s ease-in-out",
          width: "100%",
          height: "100%"
        },
        "& video": {
          width: "100% !important",
          height: "100% !important",
          objectFit: "cover",
          position: "absolute",
          top: 0,
          left: 0
        }
      }}
    />
  );
};

export default BackgroundVideo;
